import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AlertBanner from '../../components/common/AlertBanner/AlertBanner';
import Button from '../../components/common/Button/Button';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DecoratedHeader from '../../components/common/DecoratedHeader/DecoratedHeader';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Logo from '../../components/common/Logo/Logo';
import { useMealTracking } from '../../contexts/MealTrackingContext';
import type { SupplementEntry } from '../../types/mealTracking';
import CheckoutContainer from '../Checkout/components/CheckoutContainer';
import pageStyles from '../Checkout/CheckoutPage.module.css';
import fupStyles from '../Fup/Fup.module.css';
import SupplementCard from './components/SupplementCard';
import SupplementFormModal from './components/SupplementFormModal';
import { SUPPLEMENT_TRACKING_BASE_PATH } from './mealTracking.constants';
import { getTodayDateOnly, toDateOnly } from './mealTracking.utils';
import styles from './MealTracking.module.css';

type SupplementListLocationState = {
  supplementFeedback?: 'created' | 'updated';
};

const SupplementListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { getSupplementsByDate, deleteSupplement } = useMealTracking();
  const [pendingDeleteSupplement, setPendingDeleteSupplement] = useState<SupplementEntry | null>(null);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingSupplementId, setEditingSupplementId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const selectedDate = toDateOnly(searchParams.get('date') || getTodayDateOnly());
  const supplements = useMemo(
    () => getSupplementsByDate(selectedDate),
    [getSupplementsByDate, selectedDate],
  );

  useEffect(() => {
    const state = location.state as SupplementListLocationState | null;
    if (!state?.supplementFeedback) {
      return;
    }

    setSuccessMessage(
      state.supplementFeedback === 'created'
        ? 'Suplemento adicionado com sucesso.'
        : 'Suplemento atualizado com sucesso.',
    );
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const buildPath = (date = selectedDate) => {
    const params = new URLSearchParams({ date });
    return `${SUPPLEMENT_TRACKING_BASE_PATH}?${params.toString()}`;
  };

  const openCreateModal = () => {
    setSuccessMessage(null);
    setEditingSupplementId(null);
    setFormOpen(true);
  };

  const openEditModal = (supplement: SupplementEntry) => {
    setSuccessMessage(null);
    setEditingSupplementId(supplement.id);
    setFormOpen(true);
  };

  const closeFormModal = () => {
    setFormOpen(false);
    setEditingSupplementId(null);
  };

  const handleSaved = (entry: SupplementEntry, meta: { isCreate: boolean }) => {
    setFormOpen(false);
    setEditingSupplementId(null);
    navigate(buildPath(entry.date), {
      replace: true,
      state: {
        supplementFeedback: meta.isCreate ? 'created' : 'updated',
      } satisfies SupplementListLocationState,
    });
  };

  const handleDeleteConfirm = () => {
    if (!pendingDeleteSupplement) {
      return;
    }

    deleteSupplement(pendingDeleteSupplement.id);
    setPendingDeleteSupplement(null);
  };

  const handleSendConfirm = () => {
    if (supplements.length === 0 || isSending) {
      return;
    }

    setIsSending(true);
    setShowSendConfirm(false);
    setSuccessMessage('Suplementos enviados com sucesso. Obrigada por registrar o que você tomou!');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsSending(false);
  };

  return (
    <CheckoutContainer>
      <div className={styles.logoHeader}>
        <Logo className={styles.formLogo} />
        <DecoratedHeader
          className={styles.formTitle}
          title="Seus suplementos"
        />
        <p className={pageStyles.headerSubtitle}>
          Registre os suplementos que você tomou hoje: nome, quantidade e horário.
          Assim o time acompanha sua rotina com mais precisão.
        </p>
        <p className={pageStyles.headerSubtitle}>
          Quanto mais completo for o seu registro, mais fácil fica ajustar o caminho junto com você.
        </p>
      </div>

      {successMessage && (
        <div className={styles.feedbackWrap}>
          <AlertBanner variant="success" message={successMessage} />
        </div>
      )}

      <div className={`${styles.centeredLayout} ${styles.listWithFixedSend}`}>
        <article className={`${pageStyles.checkoutCard} ${styles.centeredCard} ${styles.flatCard}`}>
          {supplements.length === 0 ? (
            <div className={styles.emptyWrap}>
              <EmptyState
                message="Nenhum suplemento registrado ainda."
                icon="medication"
                showImage={false}
                compact
              />
            </div>
          ) : (
            <div className={styles.list}>
              {supplements.map((supplement) => (
                <SupplementCard
                  key={supplement.id}
                  supplement={supplement}
                  onEdit={() => openEditModal(supplement)}
                  onDelete={() => setPendingDeleteSupplement(supplement)}
                />
              ))}
            </div>
          )}

          <div className={fupStyles.footerActions}>
            <Button
              type="button"
              variant="outline"
              size="app"
              fullWidth
              iconRight="add"
              onClick={openCreateModal}
            >
              Adicionar suplemento
            </Button>
          </div>
        </article>
      </div>

      {!formOpen && (
        <div className={`${pageStyles.footerFixed} ${styles.sendFooter}`}>
          <div className={styles.sendFooterInner}>
            <Button
              type="button"
              variant="primary"
              size="app"
              fullWidth
              iconRight="arrow_forward"
              disabled={supplements.length === 0 || isSending}
              onClick={() => setShowSendConfirm(true)}
            >
              Enviar suplementos
            </Button>
          </div>
        </div>
      )}

      <SupplementFormModal
        open={formOpen}
        supplementId={editingSupplementId}
        defaultDate={selectedDate}
        onClose={closeFormModal}
        onSaved={handleSaved}
      />

      <ConfirmModal
        open={Boolean(pendingDeleteSupplement)}
        title="Excluir suplemento"
        message="Deseja excluir este suplemento da sua lista?"
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setPendingDeleteSupplement(null)}
        onConfirm={handleDeleteConfirm}
      />

      <ConfirmModal
        open={showSendConfirm}
        title="Enviar suplementos"
        message="Deseja mesmo enviar seus suplementos? Depois de confirmar, o time poderá revisar o que você registrou."
        confirmLabel="Enviar suplementos"
        cancelLabel="Revisar"
        variant="info"
        icon="send"
        loading={isSending}
        onCancel={() => {
          if (!isSending) {
            setShowSendConfirm(false);
          }
        }}
        onConfirm={handleSendConfirm}
      />
    </CheckoutContainer>
  );
};

export default SupplementListPage;
